import { resolveAppPath } from '$lib/paths';

const API_BASE = process.env.NEXT_PUBLIC_API_BASE || 'http://localhost:8080/api';

interface ApiError {
	code: string;
	message: string;
}

interface ApiResponse<T> {
	success: boolean;
	data: T;
	error: ApiError | null;
}

function isApiResponse<T>(value: unknown): value is ApiResponse<T> {
	return (
		typeof value === 'object' &&
		value !== null &&
		'success' in value &&
		typeof (value as { success: unknown }).success === 'boolean'
	);
}

/**
 * 세션/임퍼소네이션 만료 시 네비게이션을 React/Next 앱 레이어에 위임하는 콜백.
 * 하드 네비게이션(window.location)은 Capacitor WebView 에서 번들을 통째로 리로드해
 * 플러그인 브리지·SPA 상태가 초기화되므로(화이트스크린), auth 레이어가 goto 로 처리한다.
 * 미등록(예: SSR·테스트) 시에는 안전한 폴백으로 동작한다.
 */
export type SessionExpiryHandlers = {
	onUnauthorized: () => void;
	onImpersonationExpired: () => void;
};

let sessionHandlers: SessionExpiryHandlers | null = null;

export function setSessionExpiryHandlers(handlers: SessionExpiryHandlers | null) {
	sessionHandlers = handlers;
}

export function getErrorMessage(error: unknown, fallback = '요청을 처리하지 못했습니다.'): string {
	if (error instanceof Error && error.message) return error.message;
	if (typeof error === 'string' && error) return error;
	return fallback;
}

class ApiClient {
	private getToken(): string | null {
		if (typeof window === 'undefined') return null;
		return localStorage.getItem('impersonationToken') || localStorage.getItem('accessToken');
	}

	private isImpersonating(): boolean {
		if (typeof window === 'undefined') return false;
		return localStorage.getItem('impersonationToken') !== null;
	}

	private getRefreshToken(): string | null {
		if (typeof window === 'undefined') return null;
		return localStorage.getItem('refreshToken');
	}

	setTokens(accessToken: string, refreshToken: string) {
		localStorage.setItem('accessToken', accessToken);
		localStorage.setItem('refreshToken', refreshToken);
	}

	clearTokens() {
		localStorage.removeItem('accessToken');
		localStorage.removeItem('refreshToken');
	}

	clearImpersonation() {
		localStorage.removeItem('impersonationToken');
		localStorage.removeItem('impersonationOriginalToken');
		localStorage.removeItem('impersonationTargetCompany');
		localStorage.removeItem('impersonationExpiresAt');
	}

	/** 인증 만료 → 토큰 정리 후 로그인 화면으로(콜백 위임, 폴백은 하드 네비게이션). */
	private handleSessionExpired() {
		this.clearTokens();
		if (sessionHandlers) sessionHandlers.onUnauthorized();
		else if (typeof window !== 'undefined') window.location.href = resolveAppPath('/login');
	}

	/** 임퍼소네이션 만료 → 임퍼소네이션 정리 후 SUPER_ADMIN 컨텍스트 복귀(콜백 위임, 폴백은 리로드). */
	private handleImpersonationExpired() {
		this.clearImpersonation();
		if (sessionHandlers) sessionHandlers.onImpersonationExpired();
		else if (typeof window !== 'undefined') window.location.reload();
	}

	private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
		const token = this.getToken();
		const headers: Record<string, string> = {
			'Content-Type': 'application/json',
			...((options.headers as Record<string, string>) || {})
		};

		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		const response = await fetch(`${API_BASE}${path}`, {
			...options,
			headers
		});

		if (response.status === 401 && token) {
			// impersonation 토큰은 refresh 미지원 — 만료 시 자동 복귀
			if (this.isImpersonating()) {
				this.handleImpersonationExpired();
				throw new Error('임패서네이션 토큰이 만료되었습니다. SUPER_ADMIN 모드로 복귀합니다.');
			}
			const refreshed = await this.tryRefresh();
			if (refreshed) {
				headers['Authorization'] = `Bearer ${this.getToken()}`;
				const retryResponse = await fetch(`${API_BASE}${path}`, { ...options, headers });
				return this.handleResponse<T>(retryResponse);
			}
			this.handleSessionExpired();
			throw new Error('인증이 만료되었습니다.');
		}

		return this.handleResponse<T>(response);
	}

	private async handleResponse<T>(response: Response): Promise<T> {
		const text = await response.text();

		if (!text) {
			if (response.ok) return undefined as T;
			throw new Error(`요청 실패 (${response.status})`);
		}

		let body: unknown;
		try {
			body = JSON.parse(text);
		} catch {
			if (!response.ok) {
				throw new Error(`요청 실패 (${response.status})`);
			}
			throw new Error('서버 응답을 해석하지 못했습니다.');
		}

		if (!isApiResponse<T>(body)) {
			throw new Error(
				response.ok ? '서버 응답 형식이 올바르지 않습니다.' : `요청 실패 (${response.status})`
			);
		}
		if (!response.ok || !body.success) {
			const message = body.error?.message || `요청 실패 (${response.status})`;
			throw new Error(message);
		}

		return body.data;
	}

	private async tryRefresh(): Promise<boolean> {
		const refreshToken = this.getRefreshToken();
		if (!refreshToken) return false;

		try {
			const response = await fetch(`${API_BASE}/auth/refresh`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ refreshToken })
			});

			if (!response.ok) return false;

			const body: ApiResponse<{ accessToken: string; refreshToken: string }> =
				await response.json();
			if (body.success && body.data) {
				this.setTokens(body.data.accessToken, body.data.refreshToken);
				return true;
			}
			return false;
		} catch {
			return false;
		}
	}

	get<T>(path: string) {
		return this.request<T>(path, { method: 'GET' });
	}

	post<T>(path: string, data?: unknown) {
		return this.request<T>(path, {
			method: 'POST',
			body: data === undefined ? undefined : JSON.stringify(data)
		});
	}

	patch<T>(path: string, data?: unknown) {
		return this.request<T>(path, {
			method: 'PATCH',
			body: data === undefined ? undefined : JSON.stringify(data)
		});
	}

	put<T>(path: string, data?: unknown) {
		return this.request<T>(path, {
			method: 'PUT',
			body: data === undefined ? undefined : JSON.stringify(data)
		});
	}

	delete<T>(path: string, data?: unknown) {
		return this.request<T>(path, {
			method: 'DELETE',
			body: data === undefined ? undefined : JSON.stringify(data)
		});
	}

	async downloadFile(path: string, filename: string): Promise<void> {
		const blob = await this.getBlob(path);
		await saveBlob(blob, filename);
	}

	async getBlob(path: string): Promise<Blob> {
		const token = this.getToken();
		const headers: Record<string, string> = {};

		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}

		let response = await fetch(`${API_BASE}${path}`, { method: 'GET', headers });

		if (response.status === 401 && token) {
			if (this.isImpersonating()) {
				this.handleImpersonationExpired();
				throw new Error('임패서네이션 토큰이 만료되었습니다.');
			}
			const refreshed = await this.tryRefresh();
			if (refreshed) {
				headers['Authorization'] = `Bearer ${this.getToken()}`;
				response = await fetch(`${API_BASE}${path}`, { method: 'GET', headers });
			} else {
				this.handleSessionExpired();
				throw new Error('인증이 만료되었습니다.');
			}
		}

		if (!response.ok) {
			throw new Error(`다운로드 실패 (${response.status})`);
		}

		return response.blob();
	}

	async getObjectUrl(path: string): Promise<string> {
		const blob = await this.getBlob(path);
		return window.URL.createObjectURL(blob);
	}

	async uploadFile<T>(path: string, formData: FormData): Promise<T> {
		const token = this.getToken();
		const headers: Record<string, string> = {};
		if (token) {
			headers['Authorization'] = `Bearer ${token}`;
		}
		// Content-Type 미지정 — 브라우저가 multipart boundary 설정
		let response = await fetch(`${API_BASE}${path}`, {
			method: 'POST',
			headers,
			body: formData
		});
		if (response.status === 401 && token) {
			if (this.isImpersonating()) {
				this.handleImpersonationExpired();
				throw new Error('임패서네이션 토큰이 만료되었습니다.');
			}
			const refreshed = await this.tryRefresh();
			if (refreshed) {
				headers['Authorization'] = `Bearer ${this.getToken()}`;
				response = await fetch(`${API_BASE}${path}`, { method: 'POST', headers, body: formData });
			} else {
				this.handleSessionExpired();
				throw new Error('인증이 만료되었습니다.');
			}
		}
		return this.handleResponse<T>(response);
	}
}

/**
 * Blob 저장. 웹은 a[download], 네이티브(Capacitor)는 Filesystem+Share 로 분기한다.
 * (iOS WKWebView 는 a[download] 가 무음 실패, Android WebView 도 blob 다운로드를 차단하므로
 *  네이티브에서는 파일을 캐시에 쓴 뒤 공유 시트로 저장/열기를 유도한다.)
 */
async function saveBlob(blob: Blob, filename: string): Promise<void> {
	const { isNativePlatform } = await import('$lib/native/platform');
	if (isNativePlatform()) {
		await saveBlobNative(blob, filename);
		return;
	}
	const url = window.URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = filename;
	document.body.appendChild(a);
	a.click();
	a.remove();
	window.URL.revokeObjectURL(url);
}

async function saveBlobNative(blob: Blob, filename: string): Promise<void> {
	const [{ Filesystem, Directory }, { Share }] = await Promise.all([
		import('@capacitor/filesystem'),
		import('@capacitor/share')
	]);
	const base64 = await blobToBase64(blob);
	const written = await Filesystem.writeFile({
		path: filename,
		data: base64,
		directory: Directory.Cache
	});
	await Share.share({ title: filename, url: written.uri });
}

function blobToBase64(blob: Blob): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = () => {
			const result = reader.result as string;
			// data:*/*;base64,XXXX → XXXX 만 추출
			resolve(result.slice(result.indexOf(',') + 1));
		};
		reader.onerror = reject;
		reader.readAsDataURL(blob);
	});
}

export const api = new ApiClient();
